import { useState, useCallback } from 'react'
import {
  Popover,
  ActionList,
  Button,
  Icon,
  Text,
  Badge,
  InlineStack,
  BlockStack,
  Box,
  Divider
} from '@shopify/polaris'
import {
  StoreMajor,
  ChevronDownMinor,
  GlobeMajor
} from '@shopify/polaris-icons'

import { useAuth } from '@/contexts/AuthContext'

interface StoreSwitcherProps {
  variant?: 'default' | 'compact'
}

export default function StoreSwitcher({ variant = 'default' }: StoreSwitcherProps) {
  const {
    stores,
    activeStoreId,
    activeStore,
    isUnifiedView,
    hasMultipleStores,
    switchStore
  } = useAuth()

  const [popoverActive, setPopoverActive] = useState(false)
  const [switching, setSwitching] = useState(false)

  const togglePopoverActive = useCallback(
    () => setPopoverActive((active) => !active),
    []
  )

  const handleStoreSelect = useCallback(async (storeId: string | null) => {
    if (storeId === activeStoreId) {
      setPopoverActive(false)
      return
    }

    setSwitching(true)
    try {
      await switchStore(storeId)
    } finally {
      setSwitching(false)
      setPopoverActive(false)
    }
  }, [activeStoreId, switchStore])

  // Don't render if only one store
  if (!hasMultipleStores) {
    return null
  }

  // Build display text
  const displayText = isUnifiedView
    ? 'All Accounts'
    : activeStore?.name || activeStore?.shopDomain || 'Select Store'

  const displaySubtext = isUnifiedView
    ? `${stores.length} accounts`
    : activeStore?.whatsapp?.phoneNumber || null

  // Build action list items
  const storeItems = [
    // Unified view option
    {
      content: 'All Accounts',
      helpText: `View all ${stores.length} accounts`,
      prefix: <Icon source={GlobeMajor} />,
      suffix: isUnifiedView ? <Badge status="info">Active</Badge> : null,
      onAction: () => handleStoreSelect(null)
    },
    // Divider
    { content: '', disabled: true },
    // Individual stores
    ...stores.map(store => ({
      content: store.name || store.shopDomain || 'Store',
      helpText: store.whatsapp?.phoneNumber || store.platform,
      prefix: <Icon source={StoreMajor} />,
      suffix: store.id === activeStoreId ? <Badge status="info">Active</Badge> : null,
      onAction: () => handleStoreSelect(store.id)
    }))
  ]

  const activator = (
    <Button
      onClick={togglePopoverActive}
      disclosure
      loading={switching}
      disabled={switching}
    >
      {variant === 'compact' ? (
        <InlineStack gap="100" align="center">
          <Icon source={isUnifiedView ? GlobeMajor : StoreMajor} />
          <Text variant="bodySm" as="span">
            {displayText}
          </Text>
        </InlineStack>
      ) : (
        <InlineStack gap="200" align="center" blockAlign="center">
          <Icon source={isUnifiedView ? GlobeMajor : StoreMajor} />
          <BlockStack gap="0">
            <Text variant="bodyMd" fontWeight="semibold" as="span">
              {displayText}
            </Text>
            {displaySubtext && (
              <Text variant="bodySm" color="subdued" as="span">
                {displaySubtext}
              </Text>
            )}
          </BlockStack>
        </InlineStack>
      )}
    </Button>
  )

  return (
    <Popover
      active={popoverActive}
      activator={activator}
      autofocusTarget="first-node"
      onClose={togglePopoverActive}
      preferredAlignment="left"
    >
      <Box padding="200" minWidth="280px">
        <BlockStack gap="200">
          <Text variant="headingSm" as="h3">
            Switch Account
          </Text>
          <Text variant="bodySm" color="subdued" as="p">
            Select an account to view or manage all accounts together
          </Text>
        </BlockStack>
      </Box>
      <Divider />
      <ActionList
        actionRole="menuitem"
        items={storeItems.filter(item => item.content !== '' || item.disabled)}
      />
    </Popover>
  )
}
